import { SortingFn } from "@tanstack/react-table";
import {
  TABULAR_MEMORY_FACTOR_HIGH,
  TABULAR_MEMORY_FACTOR_LOW,
} from "@via/pages/projects/runs/reports/utils/constants.ts";
import Config from "@via/utils/config.tsx";
import {
  getCortexAllowedFileExtensionsWithDot,
  RunReportResource,
} from "common";

// The pure, DOM-free parsing/formatting functions live in tabular.ts (which
// imports nothing but papaparse) so the report-table Web Worker can load them
// without pulling in Config (which reads `window` at module scope) or
// @tanstack/react-table. Imported (for local use below) and re-exported here
// so every existing caller and test keeps working unchanged.
import {
  cleanTabularDataBeforeRender,
  dropTrailingPartialLine,
  formatNumber,
  formatRowCells,
  getColumnsTabularData,
  getDelimiterTabularData,
  normalizeHeaderCells,
  parseDataRows,
} from "./tabular";

export {
  cleanTabularDataBeforeRender,
  dropTrailingPartialLine,
  formatNumber,
  formatRowCells,
  getColumnsTabularData,
  getDelimiterTabularData,
  normalizeHeaderCells,
  parseDataRows,
};

export const getReportUrl = (routePath: string): string =>
  routePath ? `${Config.HOST}${routePath}` : "";

export function downloadTextFile({
  content,
  fileName,
  mimeType,
}: {
  content: string;
  fileName: string;
  mimeType: string;
}): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function toTsv({
  headers,
  rows,
}: {
  headers: string[];
  rows: Record<string, unknown>[];
}): string {
  const normalizeCell = (value: unknown) =>
    String(value ?? "")
      .replace(/\r/g, "")
      .replace(/\t/g, " ")
      .replace(/\n/g, " ");

  const lines = [
    headers.map(normalizeCell).join("\t"),
    ...rows.map(row =>
      headers.map(header => normalizeCell(row?.[header])).join("\t")
    ),
  ];

  return `${lines.join("\n")}\n`;
}

/**
 * Processes the percent data by formatting the values and calculating the percentage based on a divider.
 * @param data - An array of records containing the data to be processed.
 * @returns An array of records with formatted values and calculated percentages.
 */
export const processPercentData = (data: Record<string, unknown>[] = []) => {
  if (data.length === 0) return data;

  const headers = Object.keys(data[0]);

  return data.map(row => {
    const formattedRow = { ...row };
    const divider = row[headers[1]] as string;

    formattedRow[headers[1]] = formatNumber(divider);

    for (let j = 2; j < headers.length; j++) {
      const currentValue = row[headers[j]] as string;

      if (!isNaN(Number(divider)) && !isNaN(Number(currentValue))) {
        const percentValue = (
          (parseFloat(currentValue) / parseFloat(divider)) *
          100
        ).toFixed(2);
        formattedRow[headers[j]] =
          `${formatNumber(currentValue)} (${percentValue}%)`;
      } else {
        formattedRow[headers[j]] = formatNumber(currentValue);
      }
    }
    return formattedRow;
  });
};

export const getDirectoryName = (
  tree: RunReportResource[],
  dir: string,
  id: string
): string => {
  for (const item of tree) {
    if (item.id === id) {
      return dir;
    }
    if (item.extension === "dir") {
      const hasReportTypes = item.reportTypes.length > 0; // helps us to distinguish to include the directory name in the path or not
      const newDir = dir
        ? `${dir}${item.name}/`
        : hasReportTypes
          ? `${item.name}/`
          : dir;

      const result = getDirectoryName(item.children ?? [], newDir, id);
      if (result) {
        return result;
      }
    }
  }
  return "";
};

/**
 * Parses a possibly display-formatted cell into a number for sorting/typing.
 * Report cells are rendered with `formatNumber`, which adds en-US thousands
 * separators ("1,234.5"); those commas otherwise make `Number()`/`parseFloat`
 * misread the value. Also handles percent-formatted cells ("45%", "12 (5%)")
 * and scientific notation ("2.34e-8"). Returns NaN for non-numeric values.
 */
export const parseNumericValue = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (value === null || typeof value === "undefined") return NaN;
  // Drop thousands separators so parseFloat does not stop at the first comma.
  const cleaned = String(value).replace(/,/g, "").trim();
  if (cleaned === "") return NaN;
  // parseFloat reads the leading numeric portion, e.g. "12 (5%)" -> 12.
  return parseFloat(cleaned);
};

export const customSortingFn = (
  columnType: "number" | "string"
): SortingFn<any> => {
  return (rowA, rowB, columnName) => {
    const valueA = rowA.getValue(columnName);
    const valueB = rowB.getValue(columnName);

    if (columnType === "number") {
      const numA = parseNumericValue(valueA);
      const numB = parseNumericValue(valueB);
      const aMissing = Number.isNaN(numA);
      const bMissing = Number.isNaN(numB);
      // Keep missing/non-numeric values grouped at the end for ascending sorts.
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return numA - numB;
    }

    // numeric: true gives natural ordering, e.g. "item2" before "item10".
    return String(valueA ?? "").localeCompare(String(valueB ?? ""), undefined, {
      numeric: true,
    });
  };
};

export const findType = (value: string): "number" | "string" => {
  if (typeof value === "undefined" || value === null) return "string";
  if (String(value).includes("%")) return "number";
  // Strip thousands separators before the numeric check so a formatted value
  // like "1,234" is detected as a number rather than falling through to text.
  if (isNaN(Number(String(value).replace(/,/g, "")))) {
    return "string";
  }
  return "number";
};

// Recursive function to find the first file
export const findFirstFile = (nodes: RunReportResource[]) => {
  for (const node of nodes) {
    // If the node is a file (not a directory), return its id
    if (node.extension !== "dir") {
      return node;
    }

    // If the node is a directory, check its children recursively
    if (node.children) {
      const result = findFirstFile(node.children);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
};

// Recursively checks if a report with the given ID exists in the report tree
export const findReportInTree = (
  nodes: RunReportResource[],
  reportId: string
): boolean => {
  for (const node of nodes) {
    if (node.id === reportId) {
      return true;
    }
    if (node.children) {
      if (findReportInTree(node.children, reportId)) {
        return true;
      }
    }
  }
  return false;
};

export const ALLOWED_EXTENSIONS = getCortexAllowedFileExtensionsWithDot();

// Moved to common so shared code can use it; re-exported for existing importers.
export { enableAiInsight } from "@via/components/cortex-drawer/ai-insight";

// Rounded for a reader, not exact: "85 MB", "1.2 GB".
export const formatApproxSize = (bytes: number): string => {
  const mb = bytes / 1048576;
  if (mb < 1024) {
    return `${Math.max(1, Math.round(mb))} MB`;
  }
  return `${(mb / 1024).toFixed(1)} GB`;
};

// Hover text for the "Load full table" button: what a full load costs.
export const describeFullTableLoad = (fileSize: number): string =>
  `This file is ${formatApproxSize(fileSize)}. Loading it in full keeps ` +
  `every row in this browser tab, which takes about ` +
  `${formatApproxSize(fileSize * TABULAR_MEMORY_FACTOR_LOW)} to ` +
  `${formatApproxSize(fileSize * TABULAR_MEMORY_FACTOR_HIGH)} of memory. ` +
  `The page stays usable while rows stream in, but the tab can slow down ` +
  `or close on a computer with little free memory. Download is the safer ` +
  `choice for very large tables.`;
